import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { VideoItem } from "@/types/video";
import { getTypeName, formatViews } from "@/utils/videoFilters";

interface HeroCarouselProps {
  items: VideoItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [toast, setToast] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (items.length <= 1) return;
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [items.length]);

  if (!items || items.length === 0) return null;

  const current = items[index];

  const goTo = (i: number) => {
    setIndex(((i % items.length) + items.length) % items.length);
  };

  const handlePlay = () => {
    setToast(true);
    window.setTimeout(() => setToast(false), 2200);
  };

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-950">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title}精彩画面`}
            title={`${item.title} - 99视频高清影视`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/55 to-foreground-950/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/30"></div>

      <div
        className="relative h-full w-full max-w-page mx-auto px-4 md:px-6 flex items-center"
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0].clientX;
        }}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return;
          const diff = e.changedTouches[0].clientX - touchStartX.current;
          if (Math.abs(diff) > 50) {
            goTo(index + (diff < 0 ? 1 : -1));
          }
          touchStartX.current = null;
        }}
      >
        <div key={current.id} className="w-full max-w-xl animate-fade-up">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-accent-500 text-accent-950">
              焦点推荐
            </span>
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-background-50/15 text-background-50 backdrop-blur-sm">
              {getTypeName(current.type)}
            </span>
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-background-50/15 text-background-50 backdrop-blur-sm">
              {current.region}
            </span>
          </div>

          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight">
            {current.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
            <span className="inline-flex items-center gap-1 text-accent-400 font-semibold">
              <i className="ri-star-fill"></i>
              {current.rating.toFixed(1)}
            </span>
            <span>{current.year}</span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-play-circle-line"></i>
              {formatViews(current.views)} 播放
            </span>
            <span>{current.duration}</span>
          </div>

          <p className="mt-4 text-sm md:text-base text-background-200/90 leading-relaxed line-clamp-2 md:line-clamp-3">
            {current.description}
          </p>

          <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/detail/${current.id}`)}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-accent-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg"></i>
              查看详情
            </button>
            <button
              type="button"
              onClick={handlePlay}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/15 text-background-50 text-sm font-medium hover:bg-background-50/25 transition-colors backdrop-blur-sm cursor-pointer whitespace-nowrap"
            >
              <i className="ri-movie-2-line text-lg"></i>
              演示播放
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2.5 z-10">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`切换到${item.title}`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/40"
            }`}
          ></button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => goTo(index - 1)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-foreground-950/30 text-background-50 items-center justify-center hover:bg-foreground-950/50 transition-colors backdrop-blur-sm cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => goTo(index + 1)}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-foreground-950/30 text-background-50 items-center justify-center hover:bg-foreground-950/50 transition-colors backdrop-blur-sm cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      {toast && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 px-4 py-2.5 rounded-md bg-foreground-950/85 text-background-50 text-sm animate-fade-in whitespace-nowrap">
          本站为演示平台，播放功能暂未接入，敬请期待
        </div>
      )}
    </section>
  );
}